/**
 * Restoring products from a backup.
 *
 * The restore runs server-side through the product-restore function so the
 * tenant is derived from a verified token and the whole operation is one
 * transaction. It reconciles rather than replaces: products in the backup are
 * matched on SKU and updated, missing ones are created, and nothing is ever
 * deleted. Product ids survive, so sales and invoice history stay attached.
 *
 * Stock quantities are deliberately not part of a restore. Stock is derived from
 * per-location inventory, so it has to be corrected through an inventory
 * adjustment where the change stays visible in history.
 */

import { authenticatedFetch } from '../hooks/useAuthenticatedFetch'
import { createContextLogger } from './logger'

const log = createContextLogger('productRestore')

export interface RestoreSummary {
	productsInBackup: number
	created: number
	updated: number
	skipped: number
}

export interface RestoreResult {
	success: boolean
	error?: string
	summary?: RestoreSummary
}

export async function restoreProductsFromBackup({
	items,
}: {
	items: unknown[]
}): Promise<RestoreResult> {
	const supabaseUrl = import.meta.env.VITE_SUPABASE_URL

	if (!supabaseUrl) return { success: false, error: 'Supabase configuration not found' }
	if (!Array.isArray(items) || items.length === 0) {
		return { success: false, error: 'This backup contains no products to restore' }
	}

	const { data, error } = await authenticatedFetch<RestoreResponse>(
		`${supabaseUrl}/functions/v1/product-restore`,
		{ method: 'POST', body: JSON.stringify({ items }) },
	)

	if (error || !data?.success) {
		log.warn('Product restore rejected', { error: error ?? 'unknown' })
		return { success: false, error: error ?? 'Failed to restore products' }
	}

	const summary: RestoreSummary = {
		productsInBackup: data.summary?.products_in_backup ?? items.length,
		created: data.summary?.created ?? 0,
		updated: data.summary?.updated ?? 0,
		skipped: data.summary?.skipped ?? 0,
	}

	log.info('Products restored', { ...summary })

	return { success: true, summary }
}

/** Ready-to-display outcome of a restore. */
export function describeRestore(summary: RestoreSummary): string {
	const parts = [
		`${summary.updated} product${summary.updated === 1 ? '' : 's'} updated`,
		`${summary.created} added`,
	]

	if (summary.skipped > 0) parts.push(`${summary.skipped} skipped (no SKU to match on)`)

	return `${parts.join(', ')}. Stock levels were not changed, and nothing was deleted.`
}

interface RestoreResponse {
	success?: boolean
	summary?: {
		products_in_backup?: number
		created?: number
		updated?: number
		skipped?: number
	}
}
